// Leer y escribir los posts del calendario —
// ADR-2026-09-15-un-lanzamiento-de-redes-es-un-calendario-de-posts.
//
// Un post ES una fila de `lanzamientos_productos` con `producto_id` en null y
// `programado_at` puesto. Por eso acá no hay altas de medios ni de comentarios:
// los medios cuelgan de esa fila igual que las fotos de un producto
// (`sumarGeneradasAProducto`, `subirImagenAProducto`, `quitarImagen`,
// `reordenarImagenes`) y los comentarios también. Todo eso se reusa tal cual.
//
// Lo único propio del calendario es: el copy, la fecha, y qué pasa cuando llega
// la hora.

import { supabase } from "../../../lib/supabase";
import { esNombreDeVideo } from "../../../utils/posterDeVideo";
import { firmarThumb } from "../lanzamientosApi";
import type { PostEnAgenda } from "./mejorMomento";
import type { EstadoDePost, MedioDePost, PostDeRedes } from "./post";

/** Qué hacer cuando llega la hora de un post. */
export type ModoDePublicacion = "avisar" | "automatico";

interface FilaDeMedio {
  id: string;
  imagen_generada_id: string | null;
  file_name: string | null;
  orden: number | null;
  imagenes_generadas?: { file_name: string } | Array<{ file_name: string }> | null;
}

/** El nombre del archivo, venga de una generación o de una subida. */
function nombreDelArchivo(m: FilaDeMedio): string {
  if (m.file_name) return m.file_name;
  const g = Array.isArray(m.imagenes_generadas) ? m.imagenes_generadas[0] : m.imagenes_generadas;
  return g?.file_name ?? "";
}

/**
 * Qué decirle al usuario cuando la consulta falla.
 *
 * El caso que de verdad pasa hoy es que la migración no esté aplicada: PostgREST
 * contesta 400 con "column ... does not exist" y, sin traducirlo, la pantalla
 * mostraba un calendario vacío como si no hubiera posts. Nombrar la causa es lo
 * que evita buscarla en el lugar equivocado.
 */
function mensajeDeError(error: { message?: string; code?: string }): string {
  const m = error.message ?? "";
  if (/does not exist|42703|PGRST204/i.test(m + (error.code ?? ""))) {
    return "Falta aplicar la migración del calendario en la base (programado_at y modo_publicacion).";
  }
  return "No se pudo cargar el calendario.";
}

const SELECT_MEDIOS =
  "lanzamientos_imagenes(id, imagen_generada_id, file_name, orden, imagenes_generadas(file_name))";

/** Un post con sus medios y las miniaturas ya firmadas. */
export interface PostConMiniaturas extends PostDeRedes {
  /** Una URL firmada por medio, en el mismo orden. Vacía = no hay póster. */
  miniaturas: string[];
  /**
   * Notas de trabajo, no se publican (ADR-2026-10-08-el-post-tiene-planificacion-y-referencias):
   * la planificación del post y sus referencias, con links.
   */
  planificacion: string;
  referencias: string;
  /** El link del post en Instagram cuando lo publicó Atlas (ADR-2026-10-28-instagram-publica-el-calendario). */
  permalink: string | null;
  /** El id del post en Instagram: con él se leen sus números ("Cómo le fue"). */
  igMediaId: string | null;
}

/**
 * Los links de Instagram de los posts publicados. Va en una consulta aparte a propósito, como
 * `destinosGuardados`: si la migración 20261028180000 todavía no está aplicada, falla SÓLO esta y el
 * calendario se ve igual que siempre.
 */
async function permalinksDe(ids: string[]): Promise<Map<string, { permalink: string | null; mediaId: string | null }>> {
  if (ids.length === 0) return new Map();
  const { data, error } = await supabase
    .from("lanzamientos_productos")
    .select("id, ig_permalink, ig_media_id")
    .in("id", ids)
    .or("ig_permalink.not.is.null,ig_media_id.not.is.null");
  if (error) return new Map();
  return new Map(
    ((data || []) as Array<{ id: string; ig_permalink: string | null; ig_media_id: string | null }>).map((r) => [
      r.id,
      { permalink: r.ig_permalink, mediaId: r.ig_media_id },
    ]),
  );
}

/**
 * Cuáles de estos posts de Instagram salieron del calendario: id de Instagram → id del post en Atlas.
 * Para el sello "Publicado desde Atlas" del dashboard (ADR-2026-10-29-dashboard-de-metricas-de-instagram).
 * Si falla, el dashboard se ve igual, sin el sello.
 */
export async function postsDeAtlasPorMedia(
  lanzamientoId: string,
  companyId: string,
  mediaIds: string[],
): Promise<Map<string, string>> {
  if (mediaIds.length === 0) return new Map();
  const { data, error } = await supabase
    .from("lanzamientos_productos")
    .select("id, ig_media_id")
    .eq("lanzamiento_id", lanzamientoId)
    .eq("id_company", companyId)
    .in("ig_media_id", mediaIds);
  if (error) return new Map();
  return new Map(((data || []) as Array<{ id: string; ig_media_id: string }>).map((r) => [r.ig_media_id, r.id]));
}

/**
 * Los posts, y si falló, POR QUÉ.
 *
 * El error se devuelve en vez de tragarse: una lista vacía porque no hay posts y
 * una lista vacía porque la consulta reventó se ven idénticas en pantalla, y esa
 * confusión ya costó una sesión entera de "no anda y no dice nada".
 */
export async function fetchPosts(
  lanzamientoId: string,
  companyId: string,
): Promise<{ posts: PostConMiniaturas[]; error: string | null }> {
  const { data, error } = await supabase
    .from("lanzamientos_productos")
    .select(`id, descripcion, planificacion, referencias, programado_at, estado, error, ${SELECT_MEDIOS}`)
    .eq("lanzamiento_id", lanzamientoId)
    .eq("id_company", companyId)
    .order("programado_at", { ascending: true });
  if (error) {
    console.error("[postsApi] posts", error);
    return { posts: [], error: mensajeDeError(error) };
  }

  const links = await permalinksDe(((data || []) as Array<{ id: string; estado?: string }>)
    .filter((p) => p.estado === "publicado")
    .map((p) => p.id));
  const posts = await Promise.all(
    ((data || []) as any[]).map(async (p) => {
      const crudos = ((p.lanzamientos_imagenes || []) as FilaDeMedio[])
        .slice()
        .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      const medios: MedioDePost[] = crudos.map((m, i) => {
        const fileName = nombreDelArchivo(m);
        return {
          id: m.id,
          generadaId: m.imagen_generada_id,
          fileName,
          esVideo: esNombreDeVideo(fileName),
          orden: i,
        };
      });
      const miniaturas = await Promise.all(
        medios.map((m) => (m.fileName ? firmarThumb(companyId, m.fileName) : "")),
      );
      return {
        id: p.id as string,
        cuando: p.programado_at ? new Date(p.programado_at as string) : null,
        texto: (p.descripcion as string) ?? "",
        planificacion: (p.planificacion as string) ?? "",
        referencias: (p.referencias as string) ?? "",
        medios,
        estado: (p.estado as EstadoDePost) ?? "pendiente",
        error: (p.error as string) ?? null,
        miniaturas,
        permalink: links.get(p.id as string)?.permalink ?? null,
        igMediaId: links.get(p.id as string)?.mediaId ?? null,
      };
    }),
  );
  return { posts, error: null };
}

/**
 * Todo lo que la EMPRESA tiene planificado, de todos sus lanzamientos de redes.
 *
 * Es contra esto que el sugeridor evita pisarse. Mirar sólo el lanzamiento
 * abierto dejaba que dos lanzamientos en paralelo cayeran el mismo día y nadie
 * se enteraba hasta que salían.
 *
 * Trae sólo lo que todavía no salió: lo que ya se publicó ocupó su lugar en el
 * pasado y no compite por un hueco futuro.
 */
export async function fetchAgendaDeLaEmpresa(
  companyId: string,
): Promise<{ agenda: PostEnAgenda[]; error: string | null }> {
  const { data, error } = await supabase
    .from("lanzamientos_productos")
    .select("id, programado_at")
    .eq("id_company", companyId)
    .not("programado_at", "is", null)
    .neq("estado", "publicado")
    .order("programado_at", { ascending: true });
  if (error) {
    console.error("[postsApi] agenda", error);
    return { agenda: [], error: mensajeDeError(error) };
  }
  return {
    agenda: ((data || []) as Array<{ id: string; programado_at: string }>).map((r) => ({
      id: r.id,
      cuando: new Date(r.programado_at),
    })),
    error: null,
  };
}

export async function crearPost(
  lanzamientoId: string,
  companyId: string,
  cuando: Date,
): Promise<{ id: string | null; error: string | null }> {
  const { data, error } = await supabase
    .from("lanzamientos_productos")
    .insert({
      lanzamiento_id: lanzamientoId,
      id_company: companyId,
      // Sin producto: un post no sale del catálogo. Es lo que la migración
      // 20260915120000 habilitó.
      producto_id: null,
      programado_at: cuando.toISOString(),
      descripcion: "",
    })
    .select("id")
    .single();
  if (error || !data) {
    console.error("[postsApi] crear post", error);
    return { id: null, error: "No se pudo crear el post" };
  }
  return { id: (data as { id: string }).id, error: null };
}

export async function guardarPost(
  id: string,
  campos: { texto?: string; cuando?: Date | null; planificacion?: string; referencias?: string },
): Promise<{ error: string | null }> {
  const cambios: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (campos.texto !== undefined) cambios.descripcion = campos.texto;
  if (campos.planificacion !== undefined) cambios.planificacion = campos.planificacion;
  if (campos.referencias !== undefined) cambios.referencias = campos.referencias;
  if (campos.cuando !== undefined) {
    cambios.programado_at = campos.cuando ? campos.cuando.toISOString() : null;
  }
  const { error } = await supabase.from("lanzamientos_productos").update(cambios).eq("id", id);
  if (error) {
    console.error("[postsApi] guardar post", error);
    return { error: "No se pudo guardar" };
  }
  // Un post que no se pudo publicar vuelve a "pendiente" cuando se lo reprograma a futuro: es lo que
  // le pide el mensaje de error ("programalo de nuevo"). Guardarlo con la fecha vieja (para cambiarle
  // el texto, por ejemplo) no le borra el motivo. Sólo si estaba en error: uno publicado no se toca
  // (ADR-2026-10-28-instagram-publica-el-calendario).
  if (campos.cuando && campos.cuando.getTime() > Date.now()) {
    const { error: alReabrir } = await supabase
      .from("lanzamientos_productos")
      .update({ estado: "pendiente", error: null })
      .eq("id", id)
      .eq("estado", "error");
    if (alReabrir) {
      console.error("[postsApi] reabrir post con error", alReabrir);
      return { error: "Se guardó la fecha, pero no se pudo volver a programar. Probá de nuevo." };
    }
  }
  return { error: null };
}

export async function borrarPost(id: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from("lanzamientos_productos").delete().eq("id", id);
  if (error) {
    console.error("[postsApi] borrar post", error);
    return { error: "No se pudo borrar el post" };
  }
  return { error: null };
}

// ── Cómo sale: aviso o automático ──────────────────────────────────────────

export async function fetchModoPublicacion(lanzamientoId: string): Promise<ModoDePublicacion> {
  // Sin la columna esto devuelve error y `data` null: se cae a "avisar", que es
  // el default de la tabla. El aviso del problema lo da la carga de los posts.
  const { data } = await supabase
    .from("lanzamientos")
    .select("modo_publicacion")
    .eq("id", lanzamientoId)
    .maybeSingle();
  return ((data as { modo_publicacion?: string } | null)?.modo_publicacion ?? "avisar") as
    ModoDePublicacion;
}

export async function setModoPublicacion(
  lanzamientoId: string,
  modo: ModoDePublicacion,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from("lanzamientos")
    .update({ modo_publicacion: modo, updated_at: new Date().toISOString() })
    .eq("id", lanzamientoId);
  if (error) {
    console.error("[postsApi] modo de publicación", error);
    return { error: "No se pudo cambiar el modo" };
  }
  return { error: null };
}

/**
 * La cuenta de Instagram conectada, si hay.
 *
 * Es la MISMA fuente que usa Configuración (`instagram_data` por empresa), para
 * que el calendario no pueda decir "conectada" mientras la otra pantalla dice
 * que no.
 */
export async function fetchCuentaDeInstagram(companyId: string): Promise<string | null> {
  const { data } = await supabase
    .from("instagram_data")
    .select("instagram_username")
    .eq("id_company", companyId)
    .maybeSingle();
  const u = (data as { instagram_username?: string } | null)?.instagram_username;
  return u && u.trim() ? u : null;
}

// ── El calendario de la marca ──────────────────────────────────────────────
// (ADR-2026-10-06-instagram-tiene-su-propia-seccion)

/** El calendario que abre la sección Instagram: un lanzamiento `redes` por marca. */
export interface CalendarioDeLaMarca {
  id: string;
  fechaLanzamiento: string | null;
  posts: number;
}

/**
 * Lo que se está resolviendo, por empresa y marca. En dev el `useEffect` corre dos
 * veces y, sin esto, la primera visita creaba dos calendarios.
 */
const resolviendo = new Map<string, Promise<CalendarioDeLaMarca | null>>();

/**
 * El calendario de Instagram de la marca, y si no tiene, lo crea.
 *
 * Antes cada calendario se creaba a mano desde "Nuevo lanzamiento" y una marca podía
 * tener varios. Si hay más de uno, gana el de la marca exacta (no uno sin marca), después
 * el que tiene más posts y, empatados, el más viejo: así nunca se esconde el que se usó.
 */
export function calendarioDeLaMarca(
  companyId: string,
  brandId: string | null,
): Promise<CalendarioDeLaMarca | null> {
  const clave = `${companyId}:${brandId ?? ""}`;
  const enCurso = resolviendo.get(clave);
  if (enCurso) return enCurso;
  const p = buscarOCrear(companyId, brandId).finally(() => resolviendo.delete(clave));
  resolviendo.set(clave, p);
  return p;
}

async function buscarOCrear(
  companyId: string,
  brandId: string | null,
): Promise<CalendarioDeLaMarca | null> {
  let consulta = supabase
    .from("lanzamientos")
    .select("id, brand_id, fecha_lanzamiento, created_at, lanzamientos_productos(id)")
    .eq("id_company", companyId)
    .eq("canal", "redes");
  // Mismo criterio que el listado de la tienda: los sin marca se ven desde cualquiera.
  consulta = brandId ? consulta.or(`brand_id.eq.${brandId},brand_id.is.null`) : consulta;
  const { data, error } = await consulta;
  if (error) {
    console.error("[postsApi] calendario de la marca", error);
    return null;
  }
  type Fila = {
    id: string;
    brand_id: string | null;
    fecha_lanzamiento: string | null;
    created_at: string;
    lanzamientos_productos: unknown[] | null;
  };
  const filas = ((data || []) as Fila[]).map((f) => ({
    ...f,
    posts: f.lanzamientos_productos?.length ?? 0,
  }));
  filas.sort(
    (a, b) =>
      Number(b.brand_id === brandId) - Number(a.brand_id === brandId) ||
      b.posts - a.posts ||
      a.created_at.localeCompare(b.created_at),
  );
  const elegido = filas[0];
  if (elegido) {
    return { id: elegido.id, fechaLanzamiento: elegido.fecha_lanzamiento, posts: elegido.posts };
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();
  const { data: nuevo, error: alCrear } = await supabase
    .from("lanzamientos")
    .insert({
      id_company: companyId,
      brand_id: brandId,
      nombre: "Instagram",
      canal: "redes",
      created_by: user?.id ?? null,
    })
    .select("id, fecha_lanzamiento")
    .single();
  if (alCrear || !nuevo) {
    console.error("[postsApi] crear el calendario de la marca", alCrear);
    return null;
  }
  return { id: nuevo.id, fechaLanzamiento: nuevo.fecha_lanzamiento, posts: 0 };
}

/**
 * Las creaciones de la empresa con esos ids, para colgarlas de un post desde el agente.
 * Filtra por empresa: un id inventado o ajeno no se cuelga de nada.
 */
/**
 * Lo que muestra una creación, en una línea, sacado de su prompt: la "Scene:" (o "Escena:") si el prompt
 * la tiene (los de los modos arrancan con "Role:" y "Objective:"), si no, el principio.
 * Es para que el agente asocie cada image_id por TEXTO y no sólo por la posición de la
 * imagen en la respuesta (ADR-2026-10-08-el-post-se-crea-desde-su-planificacion).
 */
export function queMuestra(prompt: string | null | undefined): string {
  const p = (prompt ?? '').replace(/\s+/g, ' ').trim();
  if (!p) return '';
  // "Scene:" o "Escena:", con o sin número adelante ("1. Escena: …"), hasta el rótulo siguiente.
  const escena =
    /(?:Scene|Escena):\s*([^]*?)(?:\s+(?:\d+\.\s*)?[A-ZÁÉÍÓÚ][a-záéíóú]+:\s|$)/.exec(p)?.[1]?.trim() || p;
  return escena.length > 110 ? `${escena.slice(0, 109).trimEnd()}…` : escena;
}

export async function creacionesPorId(
  companyId: string,
  ids: string[],
): Promise<Map<string, { varianteId: string | null; queMuestra: string }>> {
  const out = new Map<string, { varianteId: string | null; queMuestra: string }>();
  const unicos = Array.from(new Set(ids.filter(Boolean)));
  if (unicos.length === 0) return out;
  const { data, error } = await supabase
    .from("imagenes_generadas")
    .select("id, variante_id, prompt")
    .eq("id_company", companyId)
    .in("id", unicos);
  if (error) {
    console.error("[postsApi] creaciones por id", error);
    return out;
  }
  for (const r of (data || []) as Array<{ id: string; variante_id: string | null; prompt: string | null }>) {
    out.set(r.id, { varianteId: r.variante_id ?? null, queMuestra: queMuestra(r.prompt) });
  }
  return out;
}

/**
 * El estado de unos posts, sin miniaturas: lo consulta el calendario cada pocos segundos mientras alguno está
 * por salir, para enterarse cuando el backend lo publica sin volver a firmar todas las fotos
 * (review Greptile #225).
 */
export async function estadosDePosts(ids: string[]): Promise<Map<string, string> | null> {
  if (ids.length === 0) return new Map();
  const { data, error } = await supabase.from("lanzamientos_productos").select("id, estado").in("id", ids);
  if (error) return null;
  return new Map(((data || []) as Array<{ id: string; estado: string }>).map((r) => [r.id, r.estado]));
}
